<template>
  <div class="bf-kpis">
    <div
      class="bf-card bf-kpi"
      v-for="stat in stats"
      :key="stat.id"
    >
      <div class="bf-kpi__label">
        <span class="bf-kpi__dot" :class="`bf-kpi__dot--${dotMap[stat.class] || 'brand'}`"></span>
        {{ stat.title }}
      </div>

      <div
        class="bf-kpi__value"
        :class="{ 'bf-kpi__value--err': stat.class === 'bg3' }"
      >
        {{ stat.number }}
      </div>

      <div class="bf-kpi__caption">{{ stat.description }}</div>
    </div>

    <div class="bf-card bf-kpi bf-kpi--plan">
      <ProgressCircle :value="planConsumption" />

      <div>
        <div class="bf-kpi__label">Consumo de Integrações</div>
        <div class="bf-kpi__caption">(Percentual)</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from "vue";
import ProgressCircle from "@/components/Progressbar/ProgressCircle.vue";

import type { DashboardResponse } from "@/types/response/dashboard";
import type { StatItem } from "@/types/props/statsItem";

export default defineComponent({
  name: "IntegrationsStats",
  components: { ProgressCircle },
  props: {
    isLoading: {
      type: Boolean,
      required: false,
    },
    data: {
      type: Object as () => DashboardResponse,
      required: true,
    },
  },
  setup(props) {
    const stats = ref<StatItem[]>([]);

    const formatNumber = (number: string) => {
      return Number(number).toLocaleString();
    };

    watch(
      () => props.data,
      (newValue) => {        
        const totalSuccessLinesPerPeriod = Object.values(newValue.success_lines_per_day)
        .reduce(
          (sum, value )=> sum + value, 0
        )

        stats.value = [
          {
            id: 1,
            class: "bg2",
            icon: "flaticon-check",
            number: formatNumber(newValue.total_active?.toString()) ?? "",
            title: "Integrações Ativas",
            description: "(Total)",
          },
          {
            id: 2,
            class: "bg5",
            icon: "flaticon-close",
            number: formatNumber(newValue.total_inactive?.toString()) ?? "0",
            title: "Integrações Inativas",
            description: "(Total)",
          },
          {
            id: 3,
            class: "",
            icon: "flaticon-trend",
            number:
              formatNumber(newValue.average_success_processed_lines?.toFixed(0).toString()) ??
              "0",
            title: "Méd. Registros Integrados",
            description: "(Por dia)",
          },
          {
            id: 4,
            class: "",
            icon: "flaticon-trend",
            number:
              formatNumber(totalSuccessLinesPerPeriod.toFixed(0).toString()) ??
              "0",
            title: "Tot. Registros Integrados",
            description: "(Por período)",
          },
          {
            id: 5,
            class: "bg3",
            icon: "flaticon-alert",
            number: formatNumber(newValue.total_errors?.toString()) ?? "0",
            title: "Registros com Erros",
            description: "(Total)",
          },
        ];
      },
      { immediate: true }
    );

    // Mapeia a classe legada de cada KPI para o modificador de cor do design novo
    const dotMap: Record<string, string> = {
      bg2: "ok",
      bg5: "mut",
      bg3: "err",
      "": "brand",
    };

    return {
      stats,
      props,
      dotMap,
      planConsumption: computed(() => {
        // Mantém o valor fracionário (ex.: 0.01%) — o arredondamento para
        // inteiro escondia consumos pequenos como 0%. A formatação do rótulo
        // fica a cargo do ProgressCircle.
        const raw = Number((props.data as any)?.plan_consumption ?? 0);
        return Math.max(0, Math.min(100, raw));
      }),
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.bf-kpi {
  padding: 18px 20px;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text2);
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
    background: var(--brand2);

    &--ok {
      background: var(--ok-fg);
    }
    &--err {
      background: var(--err-fg);
    }
    &--mut {
      background: var(--mut-fg);
    }
    &--brand {
      background: var(--brand2);
    }
  }

  &__value {
    font-size: 27px;
    font-weight: 800;
    margin-top: 8px;
    letter-spacing: -0.02em;
    color: var(--text);

    &--err {
      color: var(--err-fg);
    }
  }

  &__caption {
    font-size: 11.5px;
    color: var(--text3);
    margin-top: 4px;
  }

  &--plan {
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

@media only screen and (max-width: 1199px) {
  .bf-kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media only screen and (max-width: 575px) {
  .bf-kpis {
    grid-template-columns: 1fr;
  }
}
</style>
